/**
 * Глобальна підміна вигляду `<select>` на NiceSelect (ч.965).
 *
 * ЗАДУМ. У проєкті 707 `<select>` у 242 файлах. Переписувати їх поштучно —
 * і довго, і ризиковано (кожен має свою логіку value/onChange/валідації).
 * Тому підміняємо ВИГЛЯД централізовано, а поведінку лишаємо нативну:
 * JSX-рантайм (`react/jsx-runtime`, аліас у vite.config.ts → jsxSelectPatch)
 * віддає замість типу `'select'` наш `SelectShim`.
 *
 * SelectShim рендерить ТОЙ САМИЙ `<select>` (з усіма props і `<option>`),
 * але візуально прихованим, і поруч — NiceSelect, який читає опції з нього
 * і при виборі виставляє значення ЧЕРЕЗ нативний setter + подію `change`.
 * Тому сторінка отримує рівно ту саму подію, що й раніше: жодних правок у
 * бізнес-логіці, а форми/валідація/`name` продовжують працювати.
 *
 * ЧОМУ ПРИХОВАНИЙ, А НЕ ВИДАЛЕНИЙ: `<select>` лишається джерелом істини
 * (значення, опції, disabled) і його бачать автотести та автозаповнення.
 *
 * НЕ ПІДМІНЯЄМО: `multiple` і `size > 1` (це списки, не випадашки) — там
 * нативна поведінка доречна.
 *
 * ВИМКНУТИ (налагодження): `localStorage['ui.nativeSelect'] = '1'`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import NiceSelect from './NiceSelect';

/**
 * ВАЖЛИВО (інакше нескінченна рекурсія): SelectShim сам рендерить
 * `<select>`. Якби ми писали його як JSX, патч рантайму підмінив би його
 * знову на SelectShim — і так до переповнення стека. Тому нативний
 * елемент створюємо ПРЯМИМ викликом `React.createElement`, який патч не
 * зачіпає (перехоплюється лише jsx/jsxs з react/jsx-runtime).
 */
const rawSelect = (props: Record<string, unknown>, children: React.ReactNode) =>
  React.createElement('select', props, children);

/**
 * Прапорець налагодження: `localStorage['ui.nativeSelect'] = '1'` повертає
 * нативний вигляд (читається один раз при завантаженні модуля).
 */
const FORCE_NATIVE = (() => {
  try { return localStorage.getItem('ui.nativeSelect') === '1'; }
  catch { return false; }
})();

type SelectProps = React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>;

/** Сховати нативний select, лишивши його у DOM і доступним для коду/тестів. */
const HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1, height: 1,
  opacity: 0,
  pointerEvents: 'none',
  margin: 0, padding: 0, border: 0,
  clipPath: 'inset(50%)',
};

export function SelectShim(props: SelectProps) {
  const {
    className, style, title, id, disabled, multiple, size, children,
    value, defaultValue, onChange, ...rest
  } = props;

  const ref = useRef<HTMLSelectElement>(null);
  const [version, setVersion] = useState(0);
  const [current, setCurrent] = useState<string>(String(value ?? defaultValue ?? ''));

  // Синхронізація: керований select (value ззовні) або неконтрольований
  // (значення живе у самому елементі — читаємо після кожного рендера).
  useEffect(() => {
    if (value !== undefined && value !== null) setCurrent(String(value));
    else if (ref.current) setCurrent(ref.current.value);
  });

  // Опції могли перемалюватись (довідник довантажився) → перечитати.
  useEffect(() => { setVersion((v) => v + 1); }, [children]);

  const pick = useCallback((v: string) => {
    const el = ref.current;
    if (!el) return;
    // Нативний setter + подія 'change': React отримує рівно таку саму
    // подію, як від справжнього кліку по нативному списку.
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLSelectElement.prototype, 'value')?.set;
    setter ? setter.call(el, v) : (el.value = v);
    el.dispatchEvent(new Event('change', { bubbles: true }));
    setCurrent(v);
  }, []);

  const isList = !!multiple || (size ?? 1) > 1 || FORCE_NATIVE;
  const native = rawSelect({
    ...rest,
    ref,
    id,
    disabled,
    multiple,
    size,
    value,
    defaultValue,
    onChange,
    className,
    style: isList ? style : { ...style, ...HIDDEN },
    tabIndex: isList ? undefined : -1,
  }, children);

  // Списки (multiple/size) лишаємо нативними.
  if (isList) return native;

  return (
    <span className="nice-select__wrap" style={{ display: 'block', position: 'relative' }}>
      {native}
      {/* aria-hidden саме тут: озвучує і приймає фокус нативний select
          (він лишився у дереві доступності), а NiceSelect — лише вигляд. */}
      <NiceSelect
        ariaHidden
        selectRef={ref}
        value={current}
        disabled={!!disabled}
        version={version}
        onPick={pick}
        className={className}
        style={style}
        title={title}
      />
    </span>
  );
}

